import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { alpha, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode } from 'react';
import { DropzoneRootProps } from 'react-dropzone/.';
import { useFileDragActive } from '@/hooks/useFileDragActive';

const StyledDragOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: alpha(theme.palette.info.main, 0.08),
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  pointerEvents: 'none',
}));

const StyledDragOverlayText = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.primary.main,
}));

export const DragDropUpload = ({
  children,
  disabled = false,
  dropzoneProps,
}: {
  children: ReactNode;
  disabled?: boolean;
  dropzoneProps?: DropzoneRootProps;
}) => {
  const { isDragActive, onDragOver, onDrop } = useFileDragActive();

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <Box
      {...dropzoneProps}
      onDragOver={event => {
        dropzoneProps?.onDragOver?.(event);
        onDragOver();
      }}
      onDrop={event => {
        dropzoneProps?.onDrop?.(event);
        onDrop();
      }}
      data-testid={AssetsPageObjectModel.DragDropUpload}
      sx={{
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        width: '100%',
        height: '100%',
        flex: 1,
        minWidth: 0,
        backgroundColor: isDragActive ? 'action.hover' : 'transparent',
        '&::after': isDragActive
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: '8px',
              pointerEvents: 'none',
              zIndex: 1000,
            }
          : {},
      }}
    >
      {children}
      {isDragActive && (
        <StyledDragOverlay>
          <StyledDragOverlayText>Drop files here to upload</StyledDragOverlayText>
        </StyledDragOverlay>
      )}
    </Box>
  );
};
